import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 底部常駐指令列：左邊永遠寫著「以誰的身分送出」，一個輸入框同時吃一般訊息、指令與片語。
 *
 * 這個元件**不直接打 API**：所有動作都回呼傳入的 handlers，由宿主決定打哪個 client。
 */
import { useEffect, useRef, useState } from 'react';
import { COMMAND_KEYS, projectDraft } from '../slash/draft';
import { addPhrase, PHRASE_STORAGE_KEY, readPhrases, removePhrase, writePhrases } from '../slash/phraseBook';
function phraseStorage() {
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    }
    catch {
        return null;
    }
}
function usePhraseBook() {
    const [phrases, setPhrases] = useState(() => readPhrases(phraseStorage()));
    useEffect(() => {
        const onStorage = (event) => {
            if (event.key === PHRASE_STORAGE_KEY)
                setPhrases(readPhrases(phraseStorage()));
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);
    const update = (next) => {
        writePhrases(phraseStorage(), next);
        setPhrases(next);
    };
    return {
        phrases,
        save: (text) => update(addPhrase(phrases, text)),
        unsave: (text) => update(removePhrase(phrases, text)),
    };
}
export function commandBarState(input) {
    const blockedByBusy = input.hasIdentity && input.busy;
    return {
        inputDisabled: !input.hasIdentity,
        submitDisabled: !input.hasIdentity || input.busy || input.sending || !input.hasText,
        blockedByBusy,
    };
}
export function CommandBar({ nickname, service, identities = [], disabled = false, busy = false, placeholder = '打字回覆，Enter 送出、Shift+Enter 換行（/ 找片語）', onSay, onPm, onSwitch, onUseAccount, poolAccounts = [], }) {
    const [text, setText] = useState('');
    const [notice, setNotice] = useState(null);
    const [sending, setSending] = useState(false);
    const inputRef = useRef(null);
    const phraseBook = usePhraseBook();
    // 單列輸入框隨內容長高（上限 132px），換行時不要只看到一行。
    useEffect(() => {
        const el = inputRef.current;
        if (!el)
            return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
    }, [text]);
    const projection = projectDraft(text, {
        phrases: phraseBook.phrases,
        identities,
        poolAccounts: onUseAccount ? poolAccounts : null,
    });
    /** 回傳是否成功：呼叫端要用它決定「送失敗時把草稿放回輸入框」。 */
    const run = async (action) => {
        setSending(true);
        setNotice(null);
        try {
            await action();
            return true;
        }
        catch (err) {
            setNotice(err instanceof Error ? err.message : String(err));
            return false;
        }
        finally {
            setSending(false);
        }
    };
    /**
     * 送出訊息前先清空輸入框。
     *
     * 為什麼要在 await 之前：原本是「等 onSay／onPm 回來才 setText('')」，等價於等整個送出流程
     * （含會換資料的 setState、重繪 195 列對話）跑完才清字，使用者會覺得按了沒反應。
     * 現在先把空字串排進同一個 render（與樂觀灰字同一個 commit，下一個 frame 就生效），
     * 清單預覽那些非緊急更新則由 recordOutgoing 用 startTransition 排在後面。
     * 失敗時把原字還回去（只有在使用者還沒開始打新字時），不改變「錯誤後草稿還在」的既有行為。
     */
    const clearDraft = (draft) => {
        setText('');
        return () => setText((prev) => (prev === '' ? draft : prev));
    };
    const busyHint = () => {
        setNotice('還有動作在跑（開通或批次處理），等它結束再送出；打字可以先打。');
    };
    const execute = async (action) => {
        if (disabled || sending)
            return;
        const draft = text;
        switch (action.kind) {
            case 'none':
                return;
            case 'stage':
                setText(action.text);
                setNotice(null);
                inputRef.current?.focus();
                return;
            case 'say': {
                if (busy)
                    return busyHint();
                const restoreDraft = clearDraft(draft);
                if (!(await run(() => onSay(action.text))))
                    restoreDraft();
                return;
            }
            case 'pm': {
                if (busy)
                    return busyHint();
                const restoreDraft = clearDraft(draft);
                if (!(await run(() => onPm(action.target, action.text))))
                    restoreDraft();
                return;
            }
            case 'switch':
                await run(() => onSwitch(action.id));
                setText('');
                return;
            case 'acct':
                if (!onUseAccount)
                    return;
                if (busy)
                    return busyHint();
                await run(() => onUseAccount(action.username));
                setText('');
                return;
            case 'save':
                phraseBook.save(action.text);
                setText('');
                setNotice(`已存成片語「${action.text}」`);
                return;
            case 'unsave':
                phraseBook.unsave(action.text);
                setText('');
                setNotice(`已刪掉片語「${action.text}」`);
                return;
            default: {
                const unreachable = action;
                return unreachable;
            }
        }
    };
    const state = commandBarState({
        hasIdentity: !disabled,
        busy,
        sending,
        hasText: text.trim() !== '',
    });
    const hint = notice ?? projection.hint;
    return (_jsxs("section", { className: "cui-command", "data-testid": "command-bar", "aria-label": "\u56DE\u8986\u6307\u4EE4\u5217", "aria-busy": busy || sending ? 'true' : undefined, children: [_jsxs("div", { className: "cui-command-who", children: [_jsx("span", { className: "cui-command-label", children: "\u4EE5" }), _jsx("strong", { className: "cui-command-nick", "data-testid": "command-nickname", children: nickname || '（未選身分）' }), service ? _jsx("span", { className: `cui-badge cui-badge--${service}`, children: service }) : null, _jsx("span", { className: "cui-command-label", children: "\u8EAB\u5206\u9001\u51FA" }), _jsx("span", { className: "cui-command-keys cui-mono", children: COMMAND_KEYS })] }), projection.options.length > 0 ? (_jsx("ul", { className: "cui-command-options", "data-testid": "command-options", "aria-label": "\u5019\u9078", children: projection.options.map((option) => (_jsx("li", { children: _jsx("button", { type: "button", className: "cui-command-option", "data-testid": "command-option", onMouseDown: (event) => event.preventDefault(), onClick: () => void execute(option.pick), children: option.label }) }, option.key))) })) : null, _jsxs("div", { className: "cui-command-row", children: [_jsx("textarea", { ref: inputRef, className: "cui-textarea cui-mono cui-command-input", rows: 1, value: text, placeholder: placeholder, disabled: state.inputDisabled, onChange: (event) => {
                            setText(event.target.value);
                            setNotice(null);
                        }, onKeyDown: (event) => {
                            // 中文輸入法組字中的 Enter 是在選字，不是送出。
                            if (event.nativeEvent.isComposing)
                                return;
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                void execute(projection.enter);
                            }
                            if (event.key === 'Escape') {
                                setText('');
                                setNotice(null);
                            }
                        }, "data-testid": "command-input", "aria-label": "\u8A0A\u606F\u6216\u6307\u4EE4" }), _jsx("button", { type: "button", className: "cui-btn cui-btn--primary cui-command-send", onClick: () => void execute(projection.enter), disabled: state.submitDisabled, "data-testid": "command-send", children: sending ? '送出中…' : '送出' })] }), hint ? (_jsx("p", { className: "cui-command-hint", role: "status", "data-testid": "command-hint", children: hint })) : (_jsx("p", { className: "cui-hint cui-hint--right", children: text.length > 0 ? `${text.length} 字` : 'Enter 送出 · Shift+Enter 換行' }))] }));
}
//# sourceMappingURL=CommandBar.js.map